import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useDetections } from 'src/data-capture/hooks/file-uploads/useDetections';
import { useFamilyFileActions } from 'src/data-capture/file-management/family/useFamilyFileActions';
import { useDetectedEntitiesStore } from 'src/data-capture/stores/useDetectedEntitiesStore';
import { isFileProcessing, useFileUpload } from './useFileUpload';
import { useFamilyReviewId } from './useFamilyReviewId';

export function useFileUploadWithEntities() {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId') ?? '';
  const familyId = useFamilyReviewId();
  const { clientId } = useCurrentClientIfAvailable();

  const { handleDetectionData } = useDetectedEntitiesStore();

  const {
    file,
    isLoading: isFileLoading,
    error: fileError,
    setFileResolved,
  } = useFileUpload(fileId, { autoRefreshProcessing: true, familyId });

  const { finaliseMember } = useFamilyFileActions({ familyId: familyId ?? '' });

  const {
    detections,
    isLoading: isDetectLoading,
    isFetching: isDetectFetching,
    error: detectError,
    refetchDetections,
  } = useDetections({
    fileId: file?.id,
  });

  // Detections are cached indefinitely, and a file still being processed has none written yet, so
  // a review opened during processing would keep showing the empty result it first fetched. Ask
  // again the moment the background process finishes.
  const isProcessing = isFileProcessing(file);
  const wasProcessing = useRef(isProcessing);
  useEffect(() => {
    if (wasProcessing.current && !isProcessing) void refetchDetections();
    wasProcessing.current = isProcessing;
  }, [isProcessing]);

  useEffect(() => {
    if (file != null) {
      handleDetectionData(file.id, detections);
    }
  }, [file?.id, detections, isDetectFetching]);

  const isLoading = isFileLoading || isDetectLoading;
  const error = fileError || detectError;

  const refetch = async () => {
    await refetchDetections();
  };

  const finaliseReview = async () => {
    if (familyId && file && clientId) {
      await finaliseMember(file, clientId);
      return;
    }
    await setFileResolved(true);
  };

  return {
    file,
    isLoading,
    isProcessing,
    error,
    refetch,
    finaliseReview,
  };
}
